import api from './api';

class PatientService {
  /**
   * Check if a patient exists by email and date of birth
   * @param {string} emailAddress - Patient's email address
   * @param {string} dateOfBirth - Patient's date of birth (MM/DD/YYYY format)
   * @returns {Promise} - API response
   */
  async checkPatientExists(emailAddress, dateOfBirth) {
    try {
      const response = await api.post('/screening/check-patient', {
        emailAddress,
        dateOfBirth
      });
      return response;
    } catch (error) {
      console.error('Error checking patient existence:', error);
      throw error;
    }
  }
  /**
   * Get patient queue for a booking/event (admin only)
   * @param {string} bookingId - Booking ID (previously eventId)
   * @param {Object} filters - Optional filters (status, page, limit)
   * @returns {Promise} - API response
   */
  async getPatientQueue(bookingId, filters = {}) {
    try {
      // Handle array parameters properly by JSON stringifying them
      const processedFilters = { ...filters };
      if (processedFilters.tabFilter && Array.isArray(processedFilters.tabFilter)) {
        processedFilters.tabFilter = JSON.stringify(processedFilters.tabFilter);
      }
      
      // Remove undefined values from filters
      Object.keys(processedFilters).forEach(key => {
        if (processedFilters[key] === undefined || processedFilters[key] === null) {
          delete processedFilters[key];
        }
      });
      
      const queryParams = new URLSearchParams(processedFilters).toString();
      const response = await api.get(`/visits/booking/${bookingId}?${queryParams}`);
      return response;
    } catch (error) {
      console.error('Error getting patient queue:', error);
      throw error;
    }
  }

  /**
   * Get patient queue for all bookings/events (admin only)
   * @param {Object} filters - Optional filters (status, page, limit)
   * @returns {Promise} - API response
   */
  async getAllEventsPatientQueue(filters = {}) {
    try {
      // Handle array parameters properly by JSON stringifying them
      const processedFilters = { ...filters };
      if (processedFilters.tabFilter && Array.isArray(processedFilters.tabFilter)) {
        processedFilters.tabFilter = JSON.stringify(processedFilters.tabFilter);
      }
      
      // Remove undefined values from filters
      Object.keys(processedFilters).forEach(key => {
        if (processedFilters[key] === undefined || processedFilters[key] === null) {
          delete processedFilters[key];
        }
      });
      
      const queryParams = new URLSearchParams(processedFilters).toString();
      const fullUrl = `/visits/all?${queryParams}`;
      
      const response = await api.get(fullUrl);
      return response;
    } catch (error) {
      console.error('Error getting patient queue for all events:', error);
      throw error;
    }
  }

  /**
   * Get ALL visits matching current filters for export (no pagination cap).
   * @param {Object} filters - Filters (tabFilter, search, eventTitle, dateFrom, dateTo, sort)
   * @returns {Promise} - API response with full data array
   */
  async getAllForExport(filters = {}) {
    try {
      const processedFilters = { ...filters };
      if (processedFilters.tabFilter && Array.isArray(processedFilters.tabFilter)) {
        processedFilters.tabFilter = JSON.stringify(processedFilters.tabFilter);
      }
      // Remove pagination keys — export endpoint has none
      delete processedFilters.page;
      delete processedFilters.limit;
      Object.keys(processedFilters).forEach(key => {
        if (processedFilters[key] === undefined || processedFilters[key] === null) {
          delete processedFilters[key];
        }
      });
      const queryParams = new URLSearchParams(processedFilters).toString();
      const response = await api.get(`/visits/all/export?${queryParams}`);
      return response;
    } catch (error) {
      console.error('Error getting all records for export:', error);
      throw error;
    }
  }

  /**
   * Get all patient IDs matching current filters (no pagination).
   * Used by the "Select all across pages" feature.
   */
  async getAllMatchingIds(filters = {}) {
    try {
      const processedFilters = { ...filters };
      if (processedFilters.tabFilter && Array.isArray(processedFilters.tabFilter)) {
        processedFilters.tabFilter = JSON.stringify(processedFilters.tabFilter);
      }
      delete processedFilters.page;
      delete processedFilters.limit;
      delete processedFilters.sort;
      Object.keys(processedFilters).forEach(key => {
        if (processedFilters[key] === undefined || processedFilters[key] === null) {
          delete processedFilters[key];
        }
      });
      const queryParams = new URLSearchParams(processedFilters).toString();
      const response = await api.get(`/visits/all/ids?${queryParams}`);
      return response;
    } catch (error) {
      console.error('Error getting all matching IDs:', error);
      throw error;
    }
  }

  /**
   * Get queue counts for all tabs efficiently (dedicated count endpoint)
   * @returns {Promise} - API response with counts for each tab
   */
  async getQueueCounts() {
    try {
      const response = await api.get('/visits/counts');
      return response;
    } catch (error) {
      console.error('Error getting queue counts:', error);
      throw error;
    }
  }

  /**
   * Match a patient to an existing record (admin only)
   * @param {string} patientId - New patient ID
   * @param {string} existingPatientId - Existing patient ID to match
   * @param {string} eventId - Event ID
   * @returns {Promise} - API response
   */
  async matchPatient(patientId, existingPatientId, eventId) {
    try {
      const response = await api.post('/screening/match-patient', {
        patientId,
        existingPatientId,
        eventId
      });
      return response;
    } catch (error) {
      console.error('Error matching patient:', error);
      throw error;
    }
  }

  /**
   * Get visit by patient ID and booking ID
   * @param {string} patientId - Patient ID
   * @param {string} bookingId - Booking ID
   * @returns {Promise} - API response
   */
  async getVisitByPatientAndBooking(patientId, bookingId) {
    try {
      const response = await api.get(`/visits/booking/${bookingId}`);
      const visits = response.data?.data || [];
      const visit = visits.find(v => v.patientId === patientId);
      return { data: { data: visit } };
    } catch (error) {
      console.error('Error finding visit:', error);
      throw error;
    }
  }

  /**
   * Mark patient as needing manual attention (admin only)
   * @param {string} patientId - Patient ID
   * @param {string} bookingId - Booking ID (previously eventId)
   * @param {string} reason - Reason for manual review
   * @returns {Promise} - API response
   */
  async markForAttention(patientId, eventId, reason, visitMongoId = null) {
    try {
      console.log('PatientService.markForAttention called with:', {
        patientId,
        eventId,
        reason,
        visitMongoId
      });

      // Attempt to use screening route that updates queue status server-side
      let response = null;
      try {
        response = await api.post('/screening/mark-attention', {
          patientId,
          eventId,
          reason
        });
      } catch (screeningErr) {
        console.warn('Warning: /screening/mark-attention failed; proceeding to patch Visit status directly.', screeningErr);
      }

      // Also update the Visit model status so the visits-based queue reflects the change
      if (visitMongoId) {
        try {
          await api.patch(`/visits/${visitMongoId}/status`, { status: 'needs_attention', notes: reason });
        } catch (patchErr) {
          console.warn('Warning: failed to patch Visit status to needs_attention:', patchErr);
        }
      }
  return response || { success: true };
    } catch (error) {
      console.error('Error marking patient for attention:', error);
      throw error;
    }
  }

  /**
   * Send patient back to complete form (admin only)
   * @param {string} patientId - Patient ID
   * @param {string} eventId - Event ID
   * @param {string} reason - Reason for sending back
   * @returns {Promise} - API response
   */
  async sendBackToForm(patientId, eventId, reason) {
    try {
      const response = await api.post('/screening/send-back-to-form', {
        patientId,
        eventId,
        reason
      });
      return response;
    } catch (error) {
      console.error('Error sending patient back to form:', error);
      throw error;
    }
  }

  /**
   * Search existing patients (admin only)
   * @param {string} query - Search query
   * @param {string} eventId - Optional event ID for context
   * @returns {Promise} - API response
   */
  async searchPatients(query, eventId) {
    try {
      const queryParams = new URLSearchParams({ query, eventId }).toString();
      const response = await api.get(`/screening/search-patients?${queryParams}`);
      return response;
    } catch (error) {
      console.error('Error searching patients:', error);
      throw error;
    }
  }

  /**
   * Update visit status (replaces updateQueueStatus)
   * @param {string} visitId - Visit ID  
   * @param {Object} updates - Updates to apply (status, notes, priority)
   * @returns {Promise} - API response
   */
  async updateQueueStatus(visitId, updates) {
    try {
      const response = await api.patch(`/visits/${visitId}/status`, updates);
      return response;
    } catch (error) {
      console.error('Error updating visit status:', error);
      throw error;
    }
  }

  /**
   * Update visit status by visit ID
   * @param {string} visitId - Visit ID  
   * @param {Object} updates - Updates to apply (status, notes, priority)
   * @returns {Promise} - API response
   */
  async updateVisitStatus(visitId, updates) {
    try {
      const response = await api.patch(`/visits/${visitId}/status`, updates);
      return response;
    } catch (error) {
      console.error('Error updating visit status:', error);
      throw error;
    }
  }  /**
   * Find potential duplicate patients based on simple matching criteria
   * @param {string} patientId - Patient ID to find matches for
   * @returns {Promise} - API response with potential matches
   */
  async findBasicMatches(patientId) {
    try {
      const response = await api.get(`/screening/potential-matches/${patientId}`);
      return response.data || [];
    } catch (error) {
      console.error('Error finding potential matches:', error);
      throw error;
    }
  }

  // Get all patients
  async getAllPatients() {
    try {
      const response = await api.get('/patients');
      return response;
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  }  // Get single patient by ID
  async getPatient(patientId) {
    try {
      const response = await api.get(`/patients/${patientId}`);
      return response;
    } catch (error) {
      console.error('Error fetching patient:', error);
      throw error;
    }
  }

  // Create new patient
  async createPatient(patientData) {
    try {
      const response = await api.post('/patients', patientData);
      return response;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  }

  // Members page "Add member": resolves with the response body (incl. 400/409 errors).
  createMember(memberData) {
    return api.post('/screening/members', memberData);
  }
  // Update patient
  async updatePatient(patientId, patientData) {
    try {
      const response = await api.put(`/screening/patient/${patientId}`, patientData);
      return response;
    } catch (error) {
      console.error('❌ Error updating patient:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  }

  // Update patient status
  async updatePatientStatus(patientId, status) {
    try {
      const response = await api.patch(`/patients/${patientId}/status`, { status });
      return response;
    } catch (error) {
      console.error('Error updating patient status:', error);
      throw error;
    }
  }

  // Delete patient
  async deletePatient(patientId) {
    try {
      const response = await api.delete(`/patients/${patientId}`);
      return response;
    } catch (error) {
      console.error('Error deleting patient:', error);
      throw error;
    }
  }

  // Get patient screening data
  async getPatientScreeningData(patientId) {
    try {
      const response = await api.get(`/patients/${patientId}/screening`);
      return response;
    } catch (error) {
      console.error('Error fetching patient screening data:', error);
      throw error;
    }
  }

  // Update patient screening data
  async updatePatientScreeningData(patientId, screeningData) {
    try {
      const response = await api.put(`/patients/${patientId}/screening`, screeningData);
      return response;
    } catch (error) {
      console.error('Error updating patient screening data:', error);
      throw error;
    }
  }

  // Get patient medical history
  async getPatientMedicalHistory(patientId) {
    try {
      const response = await api.get(`/patients/${patientId}/medical-history`);
      return response;
    } catch (error) {
      console.error('Error fetching patient medical history:', error);
      throw error;
    }
  }

  // Update patient medical history
  async updatePatientMedicalHistory(patientId, medicalHistory) {
    try {
      const response = await api.put(`/patients/${patientId}/medical-history`, medicalHistory);
      return response;
    } catch (error) {
      console.error('Error updating patient medical history:', error);
      throw error;
    }
  }
  // Search patients (general purpose, without event context)
  async searchPatientsByTerm(searchTerm) {
    try {
      const response = await api.get(`/patients/search?q=${encodeURIComponent(searchTerm)}`);
      return response;
    } catch (error) {
      console.error('Error searching patients:', error);
      throw error;
    }
  }

  // Get patients by event
  async getPatientsByEvent(eventId) {
    try {
      const response = await api.get(`/patients/event/${eventId}`);
      return response;
    } catch (error) {
      console.error('Error fetching patients by event:', error);
      throw error;
    }
  }

  // Check in patient
  async checkInPatient(patientId, eventId) {
    try {
      const response = await api.post(`/patients/${patientId}/checkin`, { eventId });
      return response;
    } catch (error) {
      console.error('Error checking in patient:', error);
      throw error;
    }
  }

  // Get patient consent
  async getPatientConsent(patientId) {
    try {
      const response = await api.get(`/patients/${patientId}/consent`);
      return response;
    } catch (error) {
      console.error('Error fetching patient consent:', error);
      throw error;
    }
  }

  // Update patient consent
  async updatePatientConsent(patientId, consentData) {
    try {
      const response = await api.put(`/patients/${patientId}/consent`, consentData);
      return response;
    } catch (error) {
      console.error('Error updating patient consent:', error);
      throw error;
    }
  }
  /**
   * Find potential matching patients based on patient data (more comprehensive, for admin use)
   * @param {string} patientId - Patient ID to find matches for
   * @param {string} eventId - Optional event ID for context
   * @returns {Promise} - API response with potential matches
   */
  async findPotentialMatches(patientId, eventId) {
    try {
      const queryParams = new URLSearchParams({ 
        patientId, 
        eventId: eventId || '' 
      }).toString();
      const response = await api.get(`/screening/potential-matches?${queryParams}`);
      return response.data || [];
    } catch (error) {
      console.error('Error finding potential matches:', error);
      throw error;
    }
  }

  /**
   * Get patient details for screening entry
   * @param {string} patientId - Patient ID
   * @returns {Promise} - API response with patient details
   */
  async getPatientDetails(patientId) {
    try {
      const response = await api.get(`/screening/patient/${patientId}`);
      return response;
    } catch (error) {
      console.error('Error getting patient details:', error);
      throw error;
    }
  }

  /**
   * Save screening data for a patient
   * @param {string} patientId - Patient ID
   * @param {Object} screeningData - Screening data to save
   * @returns {Promise} - API response
   */
  async saveScreeningData(patientId, screeningData) {
    try {
      const response = await api.post(`/screening/data/${patientId}`, screeningData);
      return response;
    } catch (error) {
      console.error('Error saving screening data:', error);
      throw error;
    }
  }

  /**
   * Find potential matches for a patient
   * @param {string} patientId - Patient ID to find matches for
   * @param {string} eventId - Optional event ID for context
   * @returns {Promise} - API response with potential matches
   */
  async findPotentialMatchesForPatient(patientId, eventId) {
    try {
      const queryParams = new URLSearchParams({ 
        patientId, 
        eventId: eventId || '' 
      }).toString();
      const response = await api.get(`/screening/potential-matches?${queryParams}`);
      return response.data || [];
    } catch (error) {
      console.error('Error finding potential matches:', error);
      throw error;
    }
  }

  /**
   * Save comprehensive medical testing data for a patient
   * @param {string} patientId - Patient ID
   * @param {Object} testingData - Complete medical testing data including BP, lipids, A1c, glucose, Lp(a)
   * @returns {Promise} - API response
   */
  async saveMedicalTestingData(patientId, testingData) {
    try {
      const response = await api.post(`/screening/medical-testing/${patientId}`, testingData);
      return response;
    } catch (error) {
      console.error('Error saving medical testing data:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive medical testing data for a patient
   * @param {string} patientId - Patient ID
   * @returns {Promise} - API response with complete medical testing data
   */
  async getMedicalTestingData(patientId) {
    try {
      const response = await api.get(`/screening/medical-testing/${patientId}`);
      return response;
    } catch (error) {
      console.error('Error fetching medical testing data:', error);
      throw error;
    }
  }

  /**
   * Update specific medical test results for a patient
   * @param {string} patientId - Patient ID
   * @param {string} testType - Type of test (bloodPressure, lipidPanel, a1c, glucose, lpa)
   * @param {Object} testData - Specific test data
   * @returns {Promise} - API response
   */
  async updateMedicalTestResult(patientId, testType, testData) {
    try {
      const response = await api.patch(`/screening/medical-testing/${patientId}/${testType}`, testData);
      return response;
    } catch (error) {
      console.error('Error updating medical test result:', error);
      throw error;
    }
  }

  /**
   * Save comprehensive medical history and medications for a patient
   * @param {string} patientId - Patient ID
   * @param {Object} medicalData - Medical history and medications data
   * @returns {Promise} - API response
   */
  async saveMedicalHistoryAndMedications(patientId, medicalData) {
    try {
      const response = await api.post(`/screening/medical-history/${patientId}`, medicalData);
      return response;
    } catch (error) {
      console.error('Error saving medical history and medications:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive medical history and medications for a patient
   * @param {string} patientId - Patient ID
   * @returns {Promise} - API response with medical history and medications
   */
  async getMedicalHistoryAndMedications(patientId) {
    try {
      const response = await api.get(`/screening/medical-history/${patientId}`);
      return response;
    } catch (error) {
      console.error('Error fetching medical history and medications:', error);
      throw error;
    }
  }

  /**
   * Save lifestyle and risk factors assessment for a patient
   * @param {string} patientId - Patient ID
   * @param {Object} lifestyleData - Lifestyle and risk factors data including ACE study
   * @returns {Promise} - API response
   */
  async saveLifestyleAndRiskFactors(patientId, lifestyleData) {
    try {
      const response = await api.post(`/screening/lifestyle-risk/${patientId}`, lifestyleData);
      return response;
    } catch (error) {
      console.error('Error saving lifestyle and risk factors:', error);
      throw error;
    }
  }

  /**
   * Get lifestyle and risk factors assessment for a patient
   * @param {string} patientId - Patient ID
   * @returns {Promise} - API response with lifestyle and risk factors
   */
  async getLifestyleAndRiskFactors(patientId) {
    try {
      const response = await api.get(`/screening/lifestyle-risk/${patientId}`);
      return response;
    } catch (error) {
      console.error('Error fetching lifestyle and risk factors:', error);
      throw error;
    }
  }

  /**
   * Calculate and save comprehensive risk assessment based on all data
   * @param {string} patientId - Patient ID
   * @returns {Promise} - API response with calculated risk scores
   */
  async calculateRiskAssessment(patientId) {
    try {
      const response = await api.post(`/screening/risk-assessment/${patientId}`);
      return response;
    } catch (error) {
      console.error('Error calculating risk assessment:', error);
      throw error;
    }
  }

  /**
   * Get available events/bookings that have visits for dropdown filtering
   * @param {Object} filters - Optional filters. `tabFilter` (array of statuses)
   *   scopes the events to only those with patients in the given statuses.
   * @returns {Promise} - API response with available events
   */
  async getAvailableEvents(filters = {}) {
    try {
      const processedFilters = { ...filters };
      if (Array.isArray(processedFilters.tabFilter)) {
        processedFilters.tabFilter = JSON.stringify(processedFilters.tabFilter);
      }
      Object.keys(processedFilters).forEach(key => {
        if (processedFilters[key] === undefined || processedFilters[key] === null) {
          delete processedFilters[key];
        }
      });

      const queryString = new URLSearchParams(processedFilters).toString();
      const response = await api.get(`/visits/available-events${queryString ? `?${queryString}` : ''}`);
      return response;
    } catch (error) {
      console.error('Error getting available events:', error);
      throw error;
    }
  }
}

export default new PatientService();
